import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  exportAllEmployeesExcel,
  exportSelectedEmployeesExcel,
  exportCustomColumnsEmployees,
  importEmployeeUpdateExcel,
  importEmployeePostExcel,
} from "../../../core/api/useEmployeeExport";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";

// Column mapping based on your backend data structure
const AVAILABLE_COLUMNS = [
  { key: "employee_id", label: "Employee ID", default: true },
  { key: "firstname", label: "First Name", default: true },
  { key: "lastname", label: "Last Name", default: true },
  { key: "username", label: "Username", default: true },
  { key: "email", label: "Email", default: true },
  { key: "phone", label: "Phone", default: true },
  { key: "alternative_phone", label: "Alternative Phone", default: false },
  { key: "role_name", label: "Role Name", default: true },
  { key: "is_active", label: "Is Active", default: true },
  { key: "join_date", label: "Join Date", default: true },
  { key: "company", label: "Company", default: true },
  { key: "department_name", label: "Department Name", default: true },
  { key: "designation_name", label: "Designation Name", default: true },
  { key: "about", label: "About", default: false },
  { key: "passport_no", label: "Passport No", default: false },
  { key: "passport_expiry", label: "Passport Expiry", default: false },
  { key: "nationality", label: "Nationality", default: false },
  { key: "religion", label: "Religion", default: false },
  { key: "marital_status", label: "Marital Status", default: false },
  { key: "spouse_name", label: "Spouse Name", default: false },
  { key: "number_of_children", label: "Number of Children", default: false },
  { key: "emergency_contact_1", label: "Emergency Contact 1", default: false },
  { key: "emergency_contact_2", label: "Emergency Contact 2", default: false },
  { key: "aadhar_no", label: "Aadhar No", default: false },
  { key: "pan_card", label: "PAN Card", default: false },
  { key: "blood_group", label: "Blood Group", default: false },
  { key: "gender", label: "Gender", default: false },
  { key: "date_of_birth", label: "Date of Birth", default: false },
  { key: "street_address", label: "Street Address", default: false },
  { key: "area", label: "Area", default: false },
  { key: "city_name", label: "City Name", default: false },
  { key: "state_name", label: "State Name", default: false },
  { key: "country", label: "Country", default: false },
  { key: "zip_code", label: "Zip Code", default: false },
  { key: "overall_work_exp", label: "Overall Work Experience", default: false },
];

interface EmployeeExportImportProps {
  selectedEmployeeIds?: string[];
  onRefreshData?: () => void;
}

const EmployeeExportImport: React.FC<EmployeeExportImportProps> = ({
  selectedEmployeeIds = [],
  onRefreshData,
}) => {
  const empToken = useAuthToken();
  // Modal states
  const [showCustomExportModal, setShowCustomExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Dropdown states
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);

  // Column selection state
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    AVAILABLE_COLUMNS.filter((col) => col.default).map((col) => col.key)
  );

  // Import states
  const [importType, setImportType] = useState<"update" | "post">("update");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle column selection
  const handleColumnToggle = (columnKey: string) => {
    setSelectedColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  // Handle select all/none
  const handleSelectAll = () => {
    setSelectedColumns(AVAILABLE_COLUMNS.map((col) => col.key));
  };

  const handleSelectNone = () => {
    setSelectedColumns([]);
  };

  const handleSelectDefault = () => {
    setSelectedColumns(
      AVAILABLE_COLUMNS.filter((col) => col.default).map((col) => col.key)
    );
  };

  // Export handlers
  const handleExportAll = async () => {
    await exportAllEmployeesExcel(empToken);
    setShowExportDropdown(false);
  };

  const handleExportSelected = async () => {
    if (selectedEmployeeIds.length === 0) {
      alert("Please select employees to export");
      return;
    }
    await exportSelectedEmployeesExcel(selectedEmployeeIds,empToken);
    setShowExportDropdown(false);
  };

  const handleCustomExport = async () => {
    if (selectedColumns.length === 0) {
      alert("Please select at least one column");
      return;
    }
    await exportCustomColumnsEmployees(selectedColumns,empToken);
    setShowCustomExportModal(false);
    setShowExportDropdown(false);
  };

  // Import handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      alert("Please select a file to import");
      return;
    }

    setIsUploading(true);
    try {
      if (importType === "update") {
        await importEmployeeUpdateExcel(selectedFile,empToken);
      } else {
        await importEmployeePostExcel(selectedFile,empToken);
      }

      setShowImportModal(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Refresh data if callback provided
      if (onRefreshData) {
        onRefreshData();
      }
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setIsUploading(false);
      setShowUploadDropdown(false);
    }
  };

  return (
    <>
      {/* Export/Upload Button Group */}
      <div className="me-2 mb-2">
        <div className="d-flex align-items-center border bg-white rounded p-1 gap-1">
          {/* Export Dropdown */}
          <div className="position-relative">
            <button
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 px-2"
              style={{ height: "31px" }}
              onClick={() => {
                setShowExportDropdown(!showExportDropdown);
                setShowUploadDropdown(false);
              }}
            >
              <i className="ti ti-file-export" />
              Export
            </button>
            {showExportDropdown && (
              <div
                className="dropdown-menu show dropdown-menu-end p-2 shadow"
                style={{ minWidth: "220px" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="m-0">Export Options</h6>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setShowExportDropdown(false)}
                  >
                    ×
                  </button>
                </div>
                <button className="dropdown-item" onClick={handleExportAll}>
                  <i className="ti ti-download me-2" /> Export All
                </button>
                <button
                  className="dropdown-item"
                  onClick={handleExportSelected}
                  disabled={selectedEmployeeIds.length === 0}
                >
                  <i className="ti ti-file-check me-2" /> Selected IDs (
                  {selectedEmployeeIds.length})
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowCustomExportModal(true);
                    setShowExportDropdown(false);
                  }}
                >
                  <i className="ti ti-settings me-2" /> Custom
                </button>
              </div>
            )}
          </div>

          {/* Upload Dropdown */}
          <div className="position-relative">
            <button
              className="btn btn-outline-success btn-sm d-flex align-items-center gap-1 px-2"
              style={{ height: "31px" }}
              onClick={() => {
                setShowUploadDropdown(!showUploadDropdown);
                setShowExportDropdown(false);
              }}
            >
              <i className="ti ti-upload" />
              Upload
            </button>
            {showUploadDropdown && (
              <div
                className="dropdown-menu show dropdown-menu-end p-2 shadow"
                style={{ minWidth: "220px" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="m-0">Import Options</h6>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setShowUploadDropdown(false)}
                  >
                    ×
                  </button>
                </div>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setImportType("update");
                    setShowImportModal(true);
                    setShowUploadDropdown(false);
                  }}
                >
                  <i className="ti ti-edit me-2" /> Custom Import (Update)
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setImportType("post");
                    setShowImportModal(true);
                    setShowUploadDropdown(false);
                  }}
                >
                  <i className="ti ti-plus me-2" /> All Import (Add New)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Export Modal */}
      {showCustomExportModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Custom Export - Select Columns</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCustomExportModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleSelectAll}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleSelectNone}
                    >
                      Select None
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-info"
                      onClick={handleSelectDefault}
                    >
                      Default Selection
                    </button>
                  </div>
                </div>

                <div className="row">
                  {AVAILABLE_COLUMNS.map((column) => (
                    <div key={column.key} className="col-md-4 mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`col-${column.key}`}
                          checked={selectedColumns.includes(column.key)}
                          onChange={() => handleColumnToggle(column.key)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`col-${column.key}`}
                        >
                          {column.label}
                          {column.default && (
                            <span className="badge bg-secondary ms-1">
                              Default
                            </span>
                          )}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3">
                  <small className="text-muted">
                    {selectedColumns.length} column(s) selected
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary me-2"
                  onClick={handleSelectDefault}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCustomExport}
                  disabled={selectedColumns.length === 0}
                >
                  <i className="ti ti-download me-1" />
                  Export Selected Columns
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {importType === "update"
                    ? "Update Employees"
                    : "Add New Employees"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Select Excel File</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="form-control"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                  />
                  <div className="form-text">
                    {importType === "update"
                      ? "Upload Excel file to update existing employee data"
                      : "Upload Excel file to add new employees"}
                  </div>
                </div>

                {selectedFile && (
                  <div className="alert alert-info">
                    <i className="ti ti-file-check me-1" />
                    Selected: {selectedFile.name}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary me-2"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={!selectedFile || isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-upload me-1" />
                      {importType === "update" ? "Update Data" : "Import Data"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default EmployeeExportImport;
